
import { computed, defineComponent, onMounted, PropType, ref } from "vue";
import { Message } from "@/types/conversation";
import { useConversation } from "@/composables/conversation";
import SpinnerAnimation from "@/components/SpinnerAnimation.vue";
import DOMPurify from "dompurify";
import { useWidgetTheme } from "@/composables/widgetTheme";
import { css } from "@emotion/css";

const wrapEmojis = (txt: string) => {
  const regex =
    /(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|[\ud83c[\ude01\uddff]|\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|[\ud83c[\ude32\ude02]|\ud83c\ude1a|\ud83c\ude2f|\ud83c[\ude32-\ude3a]|[\ud83c[\ude50\ude3a]|\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff])/g;
  return txt.replace(regex, function (emoji: any) {
    return '<span class="emoji">' + emoji + "</span>";
  });
};

export default defineComponent({
  name: "MessengerMessageReply",
  components: { SpinnerAnimation },
  props: {
    message: {
      type: Object as PropType<Message>,
      required: true,
    },
  },
  setup(props, { slots }) {
    const { conversation } = useConversation();
    const { theme } = useWidgetTheme();

    const hasBottomSlot = computed(() => !!slots.bottom);

    const canShowSpinner = ref(false);

    const messageIsFromAgent = computed(
      () =>
        props.message.data.authorType === "agent" ||
        props.message.data.authorType === "system"
    );
    const messageIsFromClient = computed(
      () => props.message.data.authorType === "client"
    );

    const isFromPreviousConversation = computed(() => {
      if (conversation.value?.isEnded) {
        return true;
      }

      if (
        props.message.acknowledged &&
        props.message.conversationId !== conversation.value?.id
      ) {
        return true;
      }

      return false;
    });

    const text = wrapEmojis(DOMPurify.sanitize(props.message.data.text ?? ""));

    const isMessagePending = computed(() => {
      return (
        !props.message.acknowledged &&
        props.message.data.authorType === "client" &&
        canShowSpinner.value
      );
    });

    onMounted(() => {
      setTimeout(() => {
        // Prevent showing spinner until the message remains un-acknowledged for an abnormally long time
        canShowSpinner.value = true;
      }, 3000);
    });

    const outboundStyleClass = computed(
      () => css`
        background-color: ${theme.value?.primaryColor};
        color: ${theme.value?.primaryTextColor};

        &.pending {
          filter: brightness(85%);
        }
      `
    );

    const inboundStyleClass = computed(
      () => css`
        background-color: ${theme.value?.secondaryColor};
        color: ${theme.value?.secondaryTextColor};
      `
    );

    return {
      isMessagePending,
      messageIsFromAgent,
      messageIsFromClient,
      isFromPreviousConversation,
      text,
      hasBottomSlot,
      outboundStyleClass,
      inboundStyleClass,
    };
  },
});
